import React from 'react';
import {
  Card as BaseCard,
  CardHeader,
  CardTitle,
  CardFooter,
} from './card-primitives';
import { cn } from '../../lib/utils';

/**
 * Legacy Card adapter — backward-compatible wrapper around the composable
 * Card primitives. Existing consumers import this default export unchanged.
 *
 * For new code, prefer the composable API directly:
 *   import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/card-primitives';
 */

interface CardProps {
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

const Card: React.FC<CardProps> = ({
  title,
  children,
  footer,
  className = '',
  onClick,
}) => {
  const handleKeyDown = onClick
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }
    : undefined;

  return (
    <BaseCard
      className={cn('overflow-hidden shadow-md', className)}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {title && (
        <CardHeader className="border-b border-gray-200 px-6 py-4">
          <CardTitle className="text-gray-800">{title}</CardTitle>
        </CardHeader>
      )}
      {children}
      {footer && (
        <CardFooter className="bg-gray-50 border-t border-gray-200 px-6 py-3">
          {footer}
        </CardFooter>
      )}
    </BaseCard>
  );
};

export default Card;
